import React, { useState } from "react";
import { Link } from "react-router-dom";
import type { Task, TaskType } from "@/types/task";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  PhoneCall,
  Mail,
  CalendarClock,
  CalendarCheck2,
  MessageSquare,
  CheckSquare2,
  MoreVertical,
  Clock,
  CheckCircle2,
  RotateCcw,
  Pencil,
  Trash2,
  ArrowUpRight,
  User,
  AlarmClock,
} from "lucide-react";
import { useToast } from "@/context/ToastContext";
import { api } from "@/lib/api";
import { TaskCompleteDialog } from "./TaskCompleteDialog";
import { TaskDialog } from "./TaskDialog";
import { useConfirm } from "@/context/ConfirmContext";
import { dateLocale, withClock } from "@/lib/dateTime";

interface TaskListProps {
  tasks: Task[];
  loading?: boolean;
  onRefresh: () => void;
  showAssignee?: boolean;
  emptyMessage?: string;
  hideRelated?: boolean;
}

export function TaskList({
  tasks,
  loading = false,
  onRefresh,
  showAssignee = true,
  emptyMessage = "No tasks found.",
  hideRelated = false,
}: TaskListProps) {
  const confirm = useConfirm();
  const { success, error: toastError } = useToast();

  const [completingTask, setCompletingTask] = useState<Task | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  const getTaskIcon = (type: TaskType) => {
    switch (type) {
      case "CALL":
        return <PhoneCall className="size-4 text-primary" />;
      case "EMAIL":
        return <Mail className="size-4 text-info" />;
      case "MEETING":
        return <CalendarClock className="size-4 text-accent-foreground" />;
      case "WHATSAPP":
        return <MessageSquare className="size-4 text-success" />;
      case "FOLLOW_UP":
        return <CalendarCheck2 className="size-4 text-primary" />;
      default:
        return <CheckSquare2 className="size-4 text-muted-foreground" />;
    }
  };

  const formatDue = (dueAt: string) => {
    const d = new Date(dueAt);
    const now = new Date();
    const isPast = d.getTime() < now.getTime();

    const isToday =
      d.getDate() === now.getDate() &&
      d.getMonth() === now.getMonth() &&
      d.getFullYear() === now.getFullYear();

    const timeStr = d.toLocaleTimeString(dateLocale(), withClock({ hour: "2-digit", minute: "2-digit" }));
    const dateStr = d.toLocaleDateString(dateLocale(), { month: "short", day: "numeric" });

    if (isToday) {
      return {
        label: `Today, ${timeStr}`,
        isOverdue: isPast,
        isToday: true,
      };
    }

    return {
      label: `${dateStr}, ${timeStr}`,
      isOverdue: isPast,
      isToday: false,
    };
  };

  const handleSnooze = async (task: Task, hours: number | "tomorrow" | "next_week") => {
    try {
      const now = new Date();
      let until: Date;

      if (typeof hours === "number") {
        until = new Date(now.getTime() + hours * 60 * 60 * 1000);
      } else if (hours === "tomorrow") {
        until = new Date(now);
        until.setDate(until.getDate() + 1);
        until.setHours(9, 0, 0, 0);
      } else {
        // Next Monday 9am
        until = new Date(now);
        const day = until.getDay();
        const diff = until.getDate() + (day === 0 ? 1 : 8 - day);
        until.setDate(diff);
        until.setHours(9, 0, 0, 0);
      }

      await api.patch(`/api/Task/Snooze/${task.id}`, {
        until: until.toISOString(),
      });
      success("Task Snoozed", `Rescheduled to ${until.toLocaleString()}`);
      onRefresh();
    } catch (err: any) {
      toastError("Error", err.message || "Failed to snooze task.");
    }
  };

  const handleReopen = async (task: Task) => {
    try {
      await api.patch(`/api/Task/Reopen/${task.id}`);
      success("Task Reopened", "Task marked as open.");
      onRefresh();
    } catch (err: any) {
      toastError("Error", err.message || "Failed to reopen task.");
    }
  };

  const handleDelete = async (task: Task) => {
    if (!(await confirm({ title: `Delete "${task.title}"?`, message: "This can't be undone.", confirmLabel: "Delete", destructive: true }))) return;
    try {
      await api.delete(`/api/Task/Delete/${task.id}`);
      success("Task Deleted", "The task was permanently deleted.");
      onRefresh();
    } catch (err: any) {
      toastError("Error", err.message || "Failed to delete task.");
    }
  };

  const getRecordLink = (task: Task) => {
    if (!task.related_type || !task.related_id) return null;
    switch (task.related_type) {
      case "LEAD":
        return `/deals`;
      case "DEAL":
        return `/deals/${task.related_id}`;
      case "ACCOUNT":
        return `/accounts`;
      case "CONTACT":
        return `/contacts`;
      case "QUOTE":
        return `/quotes`;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="space-y-3 py-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-16 rounded-xl border border-border bg-card/50 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="py-12 text-center border border-dashed border-border rounded-2xl bg-card/30">
        <CheckSquare2 className="size-8 text-muted-foreground mx-auto mb-2 opacity-50" />
        <p className="text-sm font-medium text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <>
      <div className="divide-y divide-border rounded-xl border border-border bg-card overflow-hidden">
        {tasks.map((t) => {
          const dueInfo = formatDue(t.due_at);
          const isDone = t.task_status === "DONE";
          const recordLink = getRecordLink(t);

          return (
            <div
              key={t.id}
              className={`p-3.5 sm:px-4 flex items-start sm:items-center justify-between gap-3 transition-colors hover:bg-muted/30 ${
                isDone ? "opacity-60 bg-muted/20" : ""
              }`}
            >
              {/* Left: Checkbox & Title */}
              <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
                <button
                  type="button"
                  onClick={() => {
                    if (isDone) {
                      handleReopen(t);
                    } else {
                      setCompletingTask(t);
                    }
                  }}
                  className={`mt-0.5 sm:mt-0 size-5 rounded-md border flex items-center justify-center transition-colors shrink-0 ${
                    isDone
                      ? "bg-success border-success text-white"
                      : "border-muted-foreground/40 hover:border-success hover:bg-success-soft text-transparent"
                  }`}
                  title={isDone ? "Click to reopen task" : "Click to complete task"}
                >
                  <CheckCircle2 className="size-3.5 fill-current" />
                </button>

                {/* Type Icon */}
                <div className="p-1.5 rounded-lg bg-muted shrink-0 hidden sm:flex">
                  {getTaskIcon(t.task_type)}
                </div>

                {/* Title & Meta */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`text-sm font-semibold truncate ${
                        isDone ? "line-through text-muted-foreground" : "text-foreground"
                      }`}
                    >
                      {t.title}
                    </span>

                    {/* Priority Badge */}
                    {t.priority === "HIGH" && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-danger-soft text-danger">
                        High
                      </span>
                    )}

                    {/* Linked Entity Pill */}
                    {!hideRelated && t.related_name && (
                      <div className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                        <span>{t.related_type}:</span>
                        {recordLink ? (
                          <Link
                            to={recordLink}
                            className="font-medium text-primary hover:underline inline-flex items-center gap-0.5"
                          >
                            <span>{t.related_name}</span>
                            <ArrowUpRight className="size-2.5" />
                          </Link>
                        ) : (
                          <span className="font-medium text-foreground">{t.related_name}</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Description / Outcome Preview */}
                  {isDone && t.outcome ? (
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                      <span className="font-medium text-success">
                        Outcome:
                      </span>{" "}
                      {t.outcome}
                    </div>
                  ) : t.description ? (
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                      {t.description}
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Right: Due Date, Assignee, Actions */}
              <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                {/* Due Time Pill */}
                <div className="text-right">
                  <div
                    className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md ${
                      isDone
                        ? "text-muted-foreground bg-muted"
                        : dueInfo.isOverdue
                        ? "text-danger bg-danger-soft"
                        : dueInfo.isToday
                        ? "text-warning bg-warning-soft"
                        : "text-muted-foreground bg-muted/60"
                    }`}
                  >
                    <Clock className="size-3" />
                    <span>{dueInfo.label}</span>
                  </div>
                </div>

                {/* Assignee (if applicable) */}
                {showAssignee && t.assigned_to && (
                  <div
                    className="hidden md:flex items-center gap-1 text-xs text-muted-foreground"
                    title={`Assigned to ${t.assigned_to.first_name || ""} ${
                      t.assigned_to.last_name || ""
                    }`}
                  >
                    <User className="size-3.5 text-muted-foreground/70" />
                    <span className="max-w-[100px] truncate">
                      {t.assigned_to.first_name || t.assigned_to.email}
                    </span>
                  </div>
                )}

                {/* Actions Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon-sm" className="size-7">
                      <MoreVertical className="size-3.5 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {!isDone ? (
                      <>
                        <DropdownMenuItem
                          onClick={() => setCompletingTask(t)}
                          className="cursor-pointer text-success font-medium"
                        >
                          <CheckCircle2 className="size-4 mr-2" />
                          <span>Complete Task</span>
                        </DropdownMenuItem>

                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-[11px] font-semibold text-muted-foreground">
                          Quick Snooze
                        </DropdownMenuLabel>
                        <DropdownMenuItem
                          onClick={() => handleSnooze(t, 1)}
                          className="cursor-pointer text-xs"
                        >
                          <AlarmClock className="size-3.5 mr-2 text-muted-foreground" />
                          <span>Snooze 1 Hour</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleSnooze(t, "tomorrow")}
                          className="cursor-pointer text-xs"
                        >
                          <AlarmClock className="size-3.5 mr-2 text-muted-foreground" />
                          <span>Tomorrow Morning (09:00)</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleSnooze(t, "next_week")}
                          className="cursor-pointer text-xs"
                        >
                          <AlarmClock className="size-3.5 mr-2 text-muted-foreground" />
                          <span>Next Week (Mon 09:00)</span>
                        </DropdownMenuItem>

                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setEditingTask(t)}
                          className="cursor-pointer text-xs"
                        >
                          <Pencil className="size-3.5 mr-2 text-muted-foreground" />
                          <span>Edit Task</span>
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <DropdownMenuItem
                        onClick={() => handleReopen(t)}
                        className="cursor-pointer text-xs"
                      >
                        <RotateCcw className="size-3.5 mr-2 text-muted-foreground" />
                        <span>Reopen Task</span>
                      </DropdownMenuItem>
                    )}

                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleDelete(t)}
                      className="cursor-pointer text-xs text-danger focus:text-danger"
                    >
                      <Trash2 className="size-3.5 mr-2" />
                      <span>Delete</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dialog for completing task with outcome */}
      {completingTask && (
        <TaskCompleteDialog
          open={!!completingTask}
          onOpenChange={(open) => !open && setCompletingTask(null)}
          task={completingTask}
          onCompleted={() => {
            setCompletingTask(null);
            onRefresh();
          }}
        />
      )}

      {/* Dialog for editing task */}
      {editingTask && (
        <TaskDialog
          open={!!editingTask}
          onOpenChange={(open) => !open && setEditingTask(null)}
          task={editingTask}
          onSaved={() => {
            setEditingTask(null);
            onRefresh();
          }}
        />
      )}
    </>
  );
}
